import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { IconPlugConnected } from '@tabler/icons-react';

import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { emailRules, passwordRules } from '@shared/constants/validation.constants';
import { useErrorHandler } from '@shared/hooks/useErrorHandler';
import type { UserListItemResponseDto, UserRoles } from '@shared/services/apiService';

import { canAssignGroup, isBuiltIn } from '../model/privileges';
import type { AccessControlApi } from '../model/useAccessControlApi';
import { useStyles } from './styles';

// Same key UserLoader and Profile listen to, so other tabs of this browser refetch /me.
const ROLE_UPDATED_KEY = 'call-agent:user-role-updated-at';
const BASE_ACCESS: UserRoles[] = ['admin', 'viewer'];

interface ProfileValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

interface UserDialogProps {
  open: boolean;
  /** `null` creates a new user. */
  user: UserListItemResponseDto | null;
  api: AccessControlApi;
  onClose: () => void;
}

/**
 * Creates a user or edits one. The name, email and password are the platform admin's to set;
 * everyone with access to this screen manages the groups. A user is always in exactly one
 * built-in group — picked by the base access — and in any number of custom groups on top.
 */
export const UserDialog: React.FC<UserDialogProps> = ({ open, user, api, onClose }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { showError } = useErrorHandler();

  const isCreate = user === null;
  const canEditProfile = api.canManageAccounts;

  const { control, handleSubmit, reset, formState } = useForm<ProfileValues>();
  const [role, setRole] = useState<UserRoles>('viewer');
  const [selected, setSelected] = useState<string[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const membership = useMemo(
    () => new Map((user?.groups ?? []).map((group) => [group.id, group.source])),
    [user],
  );
  const baseGroup = user?.groups.find(isBuiltIn);
  const baseFromDirectory = baseGroup?.source === 'directory';
  const customGroups = api.groups.filter((group) => !isBuiltIn(group));

  useEffect(() => {
    if (!open) return;
    reset({
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      email: user?.email ?? '',
      password: '',
    });
    setRole(user?.role === 'admin' ? 'admin' : 'viewer');
    setSelected(user?.groups.filter((group) => !isBuiltIn(group)).map((group) => group.id) ?? []);
    setShowPassword(false);
  }, [open, user, reset]);

  const toggle = (groupId: string) =>
    setSelected((current) =>
      current.includes(groupId) ? current.filter((id) => id !== groupId) : [...current, groupId],
    );

  const toAdd = selected.filter((id) => !membership.has(id));
  const toRemove = customGroups
    .filter((group) => membership.get(group.id) === 'manual' && !selected.includes(group.id))
    .map((group) => group.id);
  const roleChanged = !isCreate && role !== user.role;
  const hasChanges =
    isCreate || formState.isDirty || roleChanged || toAdd.length > 0 || toRemove.length > 0;

  const save = handleSubmit(async (values) => {
    const name = `${values.firstName} ${values.lastName}`.trim();
    try {
      setIsSaving(true);
      if (isCreate) {
        await api.createUser({ ...values, role, permissionGroupIds: selected });
      } else {
        if (canEditProfile && (formState.isDirty || roleChanged)) {
          // PATCH: only what changed is sent.
          const { dirtyFields } = formState;
          await api.updateUser(user.id, {
            firstName: dirtyFields.firstName ? values.firstName : undefined,
            lastName: dirtyFields.lastName ? values.lastName : undefined,
            password: values.password || undefined,
            role: roleChanged ? role : undefined,
          });
          if (roleChanged) {
            try {
              localStorage.setItem(ROLE_UPDATED_KEY, String(Date.now()));
            } catch {
              // Ignore storage quota / privacy mode errors
            }
          }
        }
        // One call per change — the API has no bulk route. Stop at the first failure so the
        // error names one group; whatever went through is already saved and shows on refetch.
        for (const groupId of toAdd) await api.attach(user.id, groupId);
        for (const groupId of toRemove) await api.detach(user.id, groupId);
      }
      toast.success(CustomSuccessToast, {
        closeButton: false,
        autoClose: 4000,
        icon: false,
        data: {
          title: t(
            isCreate
              ? 'accessControl.users.toast.createdTitle'
              : 'accessControl.users.toast.savedTitle',
          ),
          description: t(
            isCreate ? 'accessControl.users.toast.created' : 'accessControl.users.toast.saved',
            { name },
          ),
        },
      });
      onClose();
    } catch (error) {
      showError({ title: t('accessControl.users.errors.saveTitle'), error });
    } finally {
      setIsSaving(false);
    }
  });

  const baseAccessLocked = !canEditProfile || baseFromDirectory;
  const baseAccessHint = baseFromDirectory
    ? t('accessControl.users.directoryTooltip')
    : !canEditProfile
      ? t('accessControl.users.dialog.baseAccessLocked')
      : '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{ className: classes.modalPaper }}>
      <DialogTitle>
        {isCreate
          ? t('accessControl.users.dialog.createTitle')
          : t('accessControl.users.dialog.title', {
              name: `${user.firstName} ${user.lastName}`,
            })}
      </DialogTitle>
      <DialogContent dividers className={classes.modalContent}>
        {canEditProfile && (
          <Box className={classes.formGrid}>
            <Controller
              name="firstName"
              control={control}
              rules={{ required: t('accessControl.users.dialog.required') }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  size="small"
                  label={t('accessControl.users.dialog.firstName')}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="lastName"
              control={control}
              rules={{ required: t('accessControl.users.dialog.required') }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  size="small"
                  label={t('accessControl.users.dialog.lastName')}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="email"
              control={control}
              rules={isCreate ? emailRules : undefined}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  size="small"
                  className={classes.fullRow}
                  label={t('accessControl.users.dialog.email')}
                  // The API has no way to change an email yet.
                  disabled={!isCreate}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="password"
              control={control}
              // Left empty on edit, the password stays as it is.
              rules={
                isCreate
                  ? passwordRules
                  : { validate: (value, values) => !value || passwordRules.validate(value, values) }
              }
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  size="small"
                  className={classes.fullRow}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  label={t(
                    isCreate
                      ? 'accessControl.users.dialog.password'
                      : 'accessControl.users.dialog.newPassword',
                  )}
                  error={Boolean(fieldState.error)}
                  helperText={
                    fieldState.error?.message ??
                    (isCreate ? '' : t('accessControl.users.dialog.newPasswordHint'))
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={t('common.togglePasswordVisibility')}
                          onClick={() => setShowPassword((show) => !show)}
                          edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Box>
        )}

        <Box>
          <Typography variant="subtitle2">{t('accessControl.users.dialog.baseAccess')}</Typography>
          <Typography variant="caption" color="text.secondary">
            {t('accessControl.users.dialog.baseAccessHint')}
          </Typography>
          <Tooltip title={baseAccessHint} placement="top-start">
            <RadioGroup value={role} onChange={(event) => setRole(event.target.value as UserRoles)}>
              {BASE_ACCESS.map((value) => (
                <FormControlLabel
                  key={value}
                  value={value}
                  disabled={baseAccessLocked}
                  control={<Radio size="small" />}
                  className={classes.option}
                  label={
                    <Box>
                      <Box className={classes.nameCell}>
                        <Typography variant="body2">
                          {t(`accessControl.users.dialog.roles.${value}.label`)}
                        </Typography>
                        {baseFromDirectory && value === role && (
                          <Chip
                            size="small"
                            color="info"
                            icon={<IconPlugConnected size={14} />}
                            label={t('accessControl.sso')}
                          />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {t(`accessControl.users.dialog.roles.${value}.description`)}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </RadioGroup>
          </Tooltip>
        </Box>

        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {t('accessControl.users.dialog.customGroups')}
          </Typography>
          {role === 'admin' && customGroups.length > 0 && (
            <Alert severity="info" sx={{ mb: 1 }}>
              {t('accessControl.users.dialog.adminHasEverything')}
            </Alert>
          )}
          {customGroups.length === 0 && (
            <Alert severity="info">{t('accessControl.users.dialog.noCustomGroups')}</Alert>
          )}
          {customGroups.map((group) => {
            const fromDirectory = membership.get(group.id) === 'directory';
            const allowed = canAssignGroup(api.actorPrivileges, group);
            // An Admin gains nothing from a new group; one added earlier can still be removed.
            const pointless =
              role === 'admin' && !membership.has(group.id) && !selected.includes(group.id);
            const reason = fromDirectory
              ? t('accessControl.users.directoryTooltip')
              : !allowed
                ? t('accessControl.users.dialog.lockedTooltip')
                : pointless
                  ? t('accessControl.users.redundantTooltip')
                  : '';

            return (
              <Tooltip key={group.id} title={reason} placement="top-start">
                <Box>
                  <FormControlLabel
                    disabled={fromDirectory || !allowed || pointless}
                    control={
                      <Checkbox
                        checked={selected.includes(group.id)}
                        onChange={() => toggle(group.id)}
                      />
                    }
                    label={
                      <Box className={classes.nameCell}>
                        <Typography variant="body2">{group.name}</Typography>
                        {fromDirectory && (
                          <Chip
                            size="small"
                            color="info"
                            icon={<IconPlugConnected size={14} />}
                            label={t('accessControl.sso')}
                          />
                        )}
                      </Box>
                    }
                  />
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions className={classes.modalActions}>
        <Button onClick={onClose} disabled={isSaving}>
          {t('accessControl.common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={isSaving || !hasChanges}
          data-testid="user-dialog-save">
          {isSaving
            ? t('accessControl.common.saving')
            : t(isCreate ? 'accessControl.users.dialog.create' : 'accessControl.common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
